import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { PageHeader, PageSkeleton, Avatar, EmptyState } from "../../components/fields";
import { Users, Target, ChevronRight } from "lucide-react";

export default function SpedHome() {
  const { user } = useAuth();
  const [students, setStudents] = useState(null);
  const [goalCounts, setGoalCounts] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/students").then((r) => {
      setStudents(r.data);
      r.data.forEach((s) => {
        api.get(`/goals?student_id=${s.id}`).then((g) =>
          setGoalCounts((c) => ({ ...c, [s.id]: { total: g.data.filter((x) => x.status === "Active").length, due: g.data.filter((x) => x.due).length } }))
        ).catch(() => {});
      });
    });
  }, []);

  if (!students) return <PageSkeleton />;
  const firstName = (user?.name || "").split(" ")[0];

  return (
    <div className="space-y-5 stagger">
      <PageHeader title={`Welcome, ${firstName}`} subtitle="Your students — goals, documentation, and progress" />
      {students.length === 0 ? (
        <div className="bg-white border border-[#E5E9EE] rounded-2xl">
          <EmptyState icon={Users} title="No students assigned yet" hint="When TAG assigns you a classroom or students, they appear here." />
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {students.map((s) => {
            const gc = goalCounts[s.id];
            return (
              <div key={s.id} data-testid={`sped-student-${s.id}`} className="bg-white border border-[#E5E9EE] rounded-2xl p-5 shadow-[0_1px_2px_rgba(15,31,56,0.04)]">
                <div className="flex items-center gap-3">
                  <Avatar name={`${s.first_name} ${s.last_name}`} tone="mint" />
                  <div className="flex-1 min-w-0">
                    <h3 className="font-heading text-[15px] font-semibold text-[#132238] truncate">{s.first_name} {s.last_name}</h3>
                    <p className="text-xs text-[#667085]">{s.grade ? `Grade ${s.grade}` : "Grade —"} · {(s.aides || []).map((a) => a.name).join(", ") || "No aide yet"}</p>
                  </div>
                </div>
                <div className="mt-3.5 flex items-center gap-2 text-xs">
                  <span className="flex items-center gap-1.5 font-semibold text-[#087F5B] bg-[#EAF8F3] px-2.5 py-1 rounded-full">
                    <Target size={12} /> {gc ? `${gc.total} active goal${gc.total === 1 ? "" : "s"}` : "…"}
                  </span>
                  {gc?.due > 0 && <span className="font-bold text-red-700 bg-red-50 border border-red-200 px-2.5 py-1 rounded-full">{gc.due} due</span>}
                </div>
                <div className="mt-4 flex gap-2">
                  <button data-testid={`sped-goals-btn-${s.id}`} onClick={() => navigate(`/goals?student=${s.id}`)}
                    className="flex-1 text-xs font-semibold bg-[#087F5B] hover:bg-[#066548] text-white px-3 py-2.5 rounded-[10px] transition-colors duration-150">Goal Check</button>
                  <button data-testid={`sped-dashboard-btn-${s.id}`} onClick={() => navigate(`/students/${s.id}/dashboard`)}
                    className="flex-1 flex items-center justify-center gap-1 text-xs font-semibold text-[#132238] bg-white border border-[#E5E9EE] hover:bg-slate-50 px-3 py-2.5 rounded-[10px] transition-colors duration-150">
                    Logs & trends <ChevronRight size={13} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
